import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button as CrcButton } from '@crc-evans/ui-components';
import { calculateTotalCost } from '../../utils/excelGenerator';
import './MobilistDetailView.css';

// ── Equipment data (CCAB section) ──────────────────────────────
const EQUIPMENT_CATEGORIES = [
  {
    id: 'ccab',
    label: 'CCAB',
    groups: [
      {
        name: '1060CFM Compressor',
        items: ['CRCE Supply', 'Client Supply'],
      },
      {
        name: 'Desiccant Dryer',
        items: [
          'CRCE Supply',
          'Client Supply',
          'Generic',
          'Desiccant Dryer (DH MXP107)',
          'Desiccant Dryer (SD60)',
        ],
      },
      {
        name: 'Closed Cycle Blaster',
        items: [
          'Generic',
          'Closed Cycle Blaster (Nederman)',
          'Closed Cycle Blaster (LTC)',
        ],
      },
      {
        name: 'Blast Frame - Manual',
        items: [
          'Blast Frame 2" - 6"',
          'Blast Frame 6" - 12"',
          'Blast Frame 12" - 18"',
          'Blast Frame 18" - 24"',
          'Blast Frame 24" - 30"',
        ],
      },
      {
        name: 'Blast Frame - Semi Auto',
        items: [
          'Blast Frame 12" - 18"',
          'Blast Frame 18" - 24"',
          'Blast Frame 24" - 30"',
        ],
      },
    ],
  },
];

// ── Date formatter ──────────────────────────────────────────────
const formatDate = (date) => {
  const d = new Date(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

// ── Component ────────────────────────────────────────────────────
const MobilistDetailView = ({ data, onBack, onSave }) => {
  const [activePipeline, setActivePipeline] = useState(0);
  const [activeCategory, setActiveCategory] = useState('ccab');
  // selections[pipeIdx][catId][groupName] = Set of checked item names
  const [selections, setSelections] = useState({});

  const numPipelines = parseInt(data.numberOfPipelines, 10) || 1;
  const today = formatDate(new Date());

  const isChecked = (pipeIdx, catId, groupName, itemName) =>
    selections?.[pipeIdx]?.[catId]?.[groupName]?.has(itemName) || false;

  const toggleItem = (pipeIdx, catId, groupName, itemName) => {
    setSelections((prev) => {
      const set = new Set(prev?.[pipeIdx]?.[catId]?.[groupName] || []);
      if (set.has(itemName)) set.delete(itemName);
      else set.add(itemName);
      return {
        ...prev,
        [pipeIdx]: {
          ...(prev[pipeIdx] || {}),
          [catId]: {
            ...(prev[pipeIdx]?.[catId] || {}),
            [groupName]: set,
          },
        },
      };
    });
  };

  const activeCat = EQUIPMENT_CATEGORIES.find((c) => c.id === activeCategory);
  const pipelineData = data.pipelines?.[activePipeline] || {};

  return (
    <div className="mdv-page">

      {/* ── Header ── */}
      <div className="mdv-header">
        <button className="mdv-back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="mdv-title">Mobilist Details</h1>
      </div>

      {/* ── Project info bar ── */}
      <div className="mdv-info-bar">
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project Name</span>
          <span className="mdv-info-value">{data.projectName || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project No.</span>
          <span className="mdv-info-value">{data.projectNumber || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">No. of Pipelines</span>
          <span className="mdv-info-value">{numPipelines}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Project Type</span>
          <span className="mdv-info-value">{data.projectType || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Shifts</span>
          <span className="mdv-info-value">{data.shifts || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Created By</span>
          <span className="mdv-info-value">{data.createdBy || '—'}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Created Date</span>
          <span className="mdv-info-value">{today}</span>
        </div>
        <div className="mdv-info-divider" />
        <div className="mdv-info-item">
          <span className="mdv-info-label">Updated Date</span>
          <span className="mdv-info-value">{today}</span>
        </div>
      </div>

      {/* ── Pipeline tabs ── */}
      <div className="mdv-pipeline-tabs">
        {Array.from({ length: numPipelines }, (_, i) => (
          <button
            key={i}
            className={`mdv-tab${activePipeline === i ? ' mdv-tab-active' : ''}`}
            onClick={() => setActivePipeline(i)}
          >
            Line {i + 1}
          </button>
        ))}
      </div>

      {/* ── Spec bar ── */}
      <div className="mdv-spec-bar">
        <span className="mdv-spec-label">Specification :</span>
        <div className="mdv-spec-item">
          <span className="mdv-spec-field-label">Pipe Size</span>
          <span className="mdv-spec-field-value">{pipelineData.pipeSize || '—'}</span>
        </div>
        <div className="mdv-spec-item">
          <span className="mdv-spec-field-label">Wall Thickness (mm)</span>
          <span className="mdv-spec-field-value">{pipelineData.wallThickness || '—'}</span>
        </div>
        <div className="mdv-spec-item">
          <span className="mdv-spec-field-label">No. of Field Joints</span>
          <span className="mdv-spec-field-value">{pipelineData.numberOfFieldJoints || '—'}</span>
        </div>
        {pipelineData.factoryCoatingType && (
          <div className="mdv-spec-item">
            <span className="mdv-spec-field-label">Coating Type</span>
            <span className="mdv-spec-field-value">{pipelineData.factoryCoatingType}</span>
          </div>
        )}
        {pipelineData.fjcType && (
          <div className="mdv-spec-item">
            <span className="mdv-spec-field-label">FJC Type</span>
            <span className="mdv-spec-field-value">{pipelineData.fjcType}</span>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="mdv-body">

        {/* Sidebar */}
        <div className="mdv-sidebar">
          {EQUIPMENT_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`mdv-sidebar-item${activeCategory === cat.id ? ' mdv-sidebar-item-active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Equipment content */}
        <div className="mdv-content">
          {activeCat && (
            <div className="mdv-groups">
                {activeCat.groups.map((group) => (
                  <div key={group.name} className="mdv-group">
                    <div className="mdv-group-title">{group.name}</div>
                  <div className="mdv-group-items">
                    {group.items.map((item) => {
                      const checked = isChecked(activePipeline, activeCategory, group.name, item);
                      return (
                        <div
                          key={item}
                          className={`mdv-item${checked ? ' mdv-item-checked' : ''}`}
                          onClick={() => toggleItem(activePipeline, activeCategory, group.name, item)}
                        >
                          <label className="mdv-item-label">
                            <input
                              type="checkbox"
                              className="mdv-checkbox"
                              checked={checked}
                              onChange={() => {}}
                            />
                            <span>{item}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                ))}
              </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="mdv-footer">
        <CrcButton label="Cancel" variant="secondary" onClick={onBack} size="small" />
        <CrcButton
          label="Save Mobilist"
          variant="primary"
          onClick={() => {
            const totalCost = calculateTotalCost(selections, numPipelines);
            onSave({ ...data, selections, totalCost });
          }}
          size="small"
        />
      </div>
    </div>
  );
};

export default MobilistDetailView;
